import { useEffect, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { ReviewStepProps } from '@backstage/plugin-scaffolder-react';
import { computeDestructiveActions, isProjectDeletion } from '../day2';
import {
  buildDay2ReviewGroups,
  buildStandardReviewData,
  ReviewStep,
  ReviewValue,
} from './reviewModel';

/**
 * The final Review step for every template in this Backstage.
 *
 * It has two jobs and they pull in opposite directions. For Day-2 it must be
 * SHORT: only the fields that are changing, then the destructive consequences,
 * then a checkbox that has to be ticked before Create is possible. For every
 * other template it must be exactly what it always was, because those templates
 * did not ask for a new review and must not silently get one.
 *
 * Day-2 is detected the only way that is reliable — a valid `_original`
 * snapshot object in the form data. The acknowledgement lives in component
 * state and goes nowhere near the form: it gates the button, it is not an
 * input, and it is never sent to Terraform or written into the app snapshot.
 *
 * A project deletion raises the bar from a tick to typing the application's
 * name back. A checkbox is ticked by reflex; a name is not.
 */

const isDay2Form = (formData: Record<string, unknown>): boolean => {
  const original = formData._original;
  return (
    typeof original === 'object' && original !== null && !Array.isArray(original)
  );
};

const ValueCell = (props: { value: ReviewValue; side: 'before' | 'after' }) => {
  const { value, side } = props;
  const highlighted = side === 'after' ? value.added : value.removed;

  if (Array.isArray(value.display)) {
    if (value.display.length === 0) return <>—</>;
    return (
      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
        {value.display.map(entry => {
          const marked = highlighted?.includes(entry);
          return (
            <Chip
              key={entry}
              size="small"
              label={entry}
              style={
                marked
                  ? {
                      backgroundColor:
                        side === 'after' ? '#e8f5e9' : '#ffebee',
                      color: side === 'after' ? '#1b5e20' : '#b71c1c',
                      fontWeight: 600,
                    }
                  : undefined
              }
            />
          );
        })}
      </Box>
    );
  }

  return <>{value.display}</>;
};

const Day2Review = (props: {
  formData: Record<string, unknown>;
  steps: ReviewStep[];
}) => {
  const groups = useMemo(
    () =>
      buildDay2ReviewGroups(
        props.formData._original as Record<string, unknown>,
        props.formData,
        props.steps,
      ),
    [props.formData, props.steps],
  );

  if (groups.length === 0) {
    return (
      <Box mt={1} mb={2}>
        <Typography variant="body2">No configuration changes</Typography>
      </Box>
    );
  }

  return (
    <Box mt={1} mb={2}>
      {groups.map(group => (
        <Box key={group.title} mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            {group.title}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Setting</TableCell>
                <TableCell>Before</TableCell>
                <TableCell>After</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.rows.map(row => (
                <TableRow key={row.path}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>
                    <ValueCell value={row.before} side="before" />
                  </TableCell>
                  <TableCell>
                    <ValueCell value={row.after} side="after" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Box>
  );
};

/** Renders a standard-review value the way the default review always has. */
function displayStandardValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  return String(value);
}

const StandardReview = (props: {
  formData: Record<string, unknown>;
  steps: ReviewStep[];
}) => {
  const data = useMemo(
    () => buildStandardReviewData(props.formData, props.steps),
    [props.formData, props.steps],
  );

  return (
    <Box mt={1} mb={2}>
      <Table size="small">
        <TableBody>
          {Object.entries(data).map(([label, value]) => (
            <TableRow key={label}>
              <TableCell>{label}</TableCell>
              <TableCell>{displayStandardValue(value)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export const ArkanceReviewStep = (props: ReviewStepProps) => {
  const { formData, steps, handleBack, handleCreate, disableButtons } = props;
  // Memoised because the `?? {}` fallback would otherwise be a fresh object on
  // every render, re-running the diff and the destructive-action computation
  // for form data that has not actually changed.
  const data = useMemo(
    () => (formData ?? {}) as Record<string, unknown>,
    [formData],
  );
  const day2 = isDay2Form(data);
  const deletion = day2 && isProjectDeletion(data);
  const appName = String(data.app_name ?? '');

  const destructive = useMemo(
    () =>
      day2
        ? computeDestructiveActions(
            data._original as Record<string, any>,
            data as Record<string, any>,
          )
        : [],
    [day2, data],
  );

  const [acknowledged, setAcknowledged] = useState(false);
  const [typedName, setTypedName] = useState('');

  // Going back, changing something and returning must not carry an old tick
  // (or a typed name) forward onto a different set of consequences.
  const destructiveKey = destructive.join('|');
  useEffect(() => {
    setAcknowledged(false);
    setTypedName('');
  }, [destructiveKey]);

  // Both sides trimmed: a Day-1 snapshot may carry a trailing space in
  // app_name, and the field is locked on Day-2 so nobody could ever match it.
  const nameConfirmed =
    appName.trim() !== '' && typedName.trim() === appName.trim();
  const blocked =
    destructive.length > 0 && (deletion ? !nameConfirmed : !acknowledged);

  const palette = deletion
    ? { border: '#e57373', background: '#ffebee', text: '#b71c1c' }
    : { border: '#ffb74d', background: '#fff4e5', text: '#663c00' };

  return (
    <Box>
      {day2 ? (
        <Day2Review formData={data} steps={steps as ReviewStep[]} />
      ) : (
        <StandardReview formData={data} steps={steps as ReviewStep[]} />
      )}

      {destructive.length > 0 && (
        <Box
          mb={2}
          p={2}
          style={{
            border: `1px solid ${palette.border}`,
            borderRadius: 4,
            background: palette.background,
            color: palette.text,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            {deletion
              ? 'This application will be deleted'
              : 'These changes are destructive'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {destructive.map(action => (
              <li key={action}>{action}</li>
            ))}
          </ul>
          {deletion ? (
            <Box mt={2}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label={`Type the application name (${appName}) to confirm`}
                value={typedName}
                onChange={event => setTypedName(event.target.value)}
                inputProps={{ 'aria-label': 'Confirm application name' }}
                autoComplete="off"
              />
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={event => setAcknowledged(event.target.checked)}
                  inputProps={{ 'aria-label': 'Acknowledge destructive changes' }}
                />
              }
              label="I understand these resources and their data will be deleted"
            />
          )}
        </Box>
      )}

      <Button onClick={handleBack} disabled={disableButtons}>
        Back
      </Button>
      <Button
        variant="contained"
        color={deletion ? 'secondary' : 'primary'}
        onClick={handleCreate}
        disabled={disableButtons || blocked}
      >
        {deletion ? 'Delete application' : 'Create'}
      </Button>
    </Box>
  );
};
